import { LegalPage } from './LegalPage'

export function TermsPage() {
  return (
    <LegalPage title="Terms of Service" lastUpdated="June 8, 2026">
      <p>
        These Terms of Service ("Terms") govern your use of Dr. Scribey ("Service"), operated by TailwindX, LLC, a Wyoming limited liability company ("Company", "we", "us"). By using Dr. Scribey, you agree to these Terms.
      </p>

      <h2>1. Service Description</h2>
      <p>
        Dr. Scribey is an AI-powered clinical documentation tool that assists healthcare providers with generating medical notes from audio recordings. Dr. Scribey is <strong>not a medical device</strong> and is not intended to diagnose, treat, or make clinical decisions.
      </p>

      <h2>2. Eligibility</h2>
      <p>
        Dr. Scribey is intended for use by licensed healthcare professionals. By registering, you represent that you are a licensed healthcare provider or are authorized to use the Service on behalf of one.
      </p>

      <h2>3. Your Responsibilities</h2>
      <ul>
        <li>You are solely responsible for <strong>reviewing, editing, and approving all AI-generated content</strong> before using it in clinical practice.</li>
        <li>You must verify the accuracy of all generated notes, coding suggestions (ICD-10, CPT), and clinical content.</li>
        <li>You are responsible for maintaining the confidentiality of your account credentials.</li>
        <li>You must comply with all applicable laws, including HIPAA, when using the Service.</li>
        <li>You must not use the Service to process data for patients who have not consented to AI-assisted documentation where required by law.</li>
      </ul>

      <h2>4. AI-Generated Content Disclaimer</h2>
      <p>
        All content generated by Dr. Scribey — including clinical notes, coding suggestions, patient instructions, and referral letters — is <strong>advisory only</strong>. The AI may produce inaccurate, incomplete, or inappropriate content. You must exercise independent clinical judgment and verify all output before use.
      </p>
      <p>
        <strong>Coding suggestions are not billing advice.</strong> ICD-10 and CPT code suggestions are provided for reference only. You are responsible for ensuring coding accuracy and compliance with payer requirements.
      </p>

      {/* NEEDS-LEGAL-REVIEW: AI-scribe / recording-consent language below drafted by product, pending healthcare-attorney review before prod. */}
      <h2>4a. AI Scribe and Recording Consent</h2>
      <p>
        Dr. Scribey uses ambient and dictated audio to generate documentation. Capturing a clinical conversation may constitute a <strong>recording</strong> under federal and state law. <strong>Recording-consent laws vary by jurisdiction</strong>, and a number of states require the consent of <strong>all parties</strong> before a conversation may be recorded.
      </p>
      <p>
        <strong>You are solely responsible for obtaining any patient consent required in your jurisdiction</strong> before using the Service to capture an encounter. Dr. Scribey provides optional tools to help you record and document patient consent, but obtaining valid, legally sufficient consent remains your responsibility as the treating provider and the covered entity. You agree not to use the Service to record any patient who has declined AI-assisted documentation or recording.
      </p>

      <h2>5. Subscription and Payment</h2>
      <ul>
        <li><strong>Free Trial:</strong> New accounts receive 10 free encounters with full access to all features. No credit card required.</li>
        <li>Dr. Scribey is offered as a paid subscription. Current plans, tiers, and pricing are described on our <a href="/home#pricing">Pricing page</a> and in your order or subscription confirmation at the time of purchase.</li>
        <li>Subscriptions auto-renew unless cancelled before the next billing cycle.</li>
        <li>No refunds for partial months. You may cancel at any time effective at the end of your current billing period.</li>
        <li>Pricing may change with at least 30 days' advance notice.</li>
        <li>You are responsible for all applicable taxes except taxes based on our net income.</li>
      </ul>

      <h2>6. Data Ownership and Control</h2>
      <p>
        <strong>You own your data.</strong> All clinical notes, transcripts, patient records, custom templates, and other content you create using Dr. Scribey remain your property. We claim no ownership rights over your data.
      </p>
      <p>
        <strong>Audio recordings are never stored.</strong> Audio is streamed in real-time for transcription and permanently discarded once processing is complete. No audio recordings are retained on our servers or by our transcription providers at any point.
      </p>
      <p>
        <strong>Data export:</strong> You may export your data at any time through the Service or by contacting us. Upon request, we will provide your data in a standard, machine-readable format within 14 days. This right survives termination.
      </p>
      <p>
        <strong>Data deletion:</strong> You may delete any note or transcript at any time. You may also configure automatic deletion after 30, 60, or 90 days. Full account deletion is available upon request.
      </p>
      <p>
        <strong>No AI training on your data.</strong> We never use Protected Health Information to train, fine-tune, or improve our AI models.
      </p>

      <h2>7. HIPAA Compliance</h2>
      <p>
        Dr. Scribey is designed to comply with the Health Insurance Portability and Accountability Act (HIPAA) and the HITECH Act. We enter into a <a href="/baa" className="text-indigo-600 hover:text-indigo-700">Business Associate Agreement (BAA)</a> with every customer. Our handling of Protected Health Information (PHI) is governed by the BAA, our <a href="/privacy" className="text-indigo-600 hover:text-indigo-700">Privacy Policy</a>, and applicable regulations.
      </p>

      <h2>8. Intellectual Property</h2>
      <p>
        The Service, including its software, algorithms, user interface, and branding, is owned by TailwindX, LLC. Your subscription grants a limited, non-exclusive, non-transferable license to use the Service during your subscription term. You retain all rights to your clinical data and content.
      </p>

      <h2>9. Confidentiality</h2>
      <p>
        Each party agrees to maintain the confidentiality of the other party's proprietary information and to use it only for purposes related to the Service. Confidential information does not include information that is publicly available, independently developed, or received from a third party without restriction. A party may disclose confidential information if required by law, provided the other party is given reasonable notice where permitted.
      </p>

      <h2>10. Prohibited Uses</h2>
      <ul>
        <li>Using the Service for any unlawful purpose</li>
        <li>Attempting to reverse-engineer, copy, or extract the AI models or algorithms</li>
        <li>Sharing account credentials with unauthorized individuals</li>
        <li>Using the Service to generate fraudulent documentation</li>
        <li>Submitting non-clinical content or using the Service outside its intended purpose</li>
      </ul>

      <h2>11. Limitation of Liability</h2>
      <p>
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, TAILWINDX, LLC SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, REVENUE, DATA, OR USE, WHETHER BASED IN CONTRACT, TORT, OR ANY OTHER LEGAL THEORY, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
      </p>
      <p>
        <strong>Liability cap:</strong> Our total aggregate liability for all claims arising out of these Terms shall not exceed the total amount you paid us in the <strong>12 months</strong> immediately preceding the event giving rise to the claim.
      </p>
      <p>
        <strong>Exceptions:</strong> This cap does not apply to: (a) either party's indemnification obligations; (b) breach of confidentiality; (c) gross negligence or willful misconduct; or (d) our obligations under the BAA regarding PHI breaches.
      </p>
      <p>
        We are not liable for clinical outcomes resulting from use of AI-generated content. The healthcare provider assumes full responsibility for all clinical decisions.
      </p>

      <h2>12. Mutual Indemnification</h2>
      <p>
        <strong>You indemnify us</strong> against claims arising from: your use of the Service in violation of these Terms; your violation of patient privacy rights; clinical decisions based on AI-generated content; medical malpractice claims; failure to review AI-generated content; and improper handling of PHI by you or your personnel.
      </p>
      <p>
        <strong>We indemnify you</strong> against claims arising from: our infringement of third-party intellectual property rights through the Service itself; our breach of the BAA or unauthorized disclosure of PHI caused solely by our negligence or willful misconduct; and our material breach of confidentiality obligations.
      </p>
      <p>
        <strong>Procedure:</strong> The indemnified party must promptly notify the indemnifying party, give them sole control of the defense, and provide reasonable cooperation at the indemnifying party's expense.
      </p>

      <h2>13. Termination</h2>
      <ul>
        <li>You may cancel your subscription at any time through your account settings or by contacting us.</li>
        <li>We may suspend or terminate your account for violation of these Terms, non-payment (after 15 days past due), or as required by law.</li>
        <li>We may discontinue the Service entirely with <strong>90 days' advance written notice</strong>.</li>
        <li>Upon termination, your data remains available for export for <strong>90 days</strong>. After that, it is permanently deleted unless retention is required by law.</li>
        <li>Sections 4, 6, 8, 9, 11, 12, 14, and 15 survive termination.</li>
      </ul>

      <h2>14. Dispute Resolution</h2>
      <p>
        These Terms are governed by the laws of the <strong>State of Wyoming</strong>. Before filing any formal claim, both parties agree to negotiate in good faith for at least 30 days. If informal resolution fails, disputes shall be resolved by binding arbitration under AAA Commercial Arbitration Rules. Either party may seek injunctive relief for IP infringement, confidentiality breaches, or unauthorized access.
      </p>
      <p>
        <strong>Class action waiver:</strong> All disputes shall be resolved on an individual basis, not as part of a class, consolidated, or representative action.
      </p>

      <h2>15. Force Majeure</h2>
      <p>
        Neither party shall be liable for delays or failures in performance due to causes beyond reasonable control, including natural disasters, pandemics, war, government actions, power failures, internet outages, cyberattacks, or third-party service provider failures.
      </p>

      <h2>16. General Provisions</h2>
      <ul>
        <li>These Terms, the BAA, and the Privacy Policy constitute the entire agreement.</li>
        <li>Material changes to these Terms require 30 days' email notice. Continued use constitutes acceptance.</li>
        <li>If any provision is found unenforceable, it shall be limited to the minimum extent necessary.</li>
        <li>Our failure to enforce any provision does not constitute a waiver.</li>
        <li>You may not assign these Terms without our written consent. We may assign in connection with a merger, acquisition, or sale of assets.</li>
        <li>The parties are independent contractors. No partnership, joint venture, or employment relationship is created.</li>
        <li>These Terms do not create third-party beneficiary rights.</li>
      </ul>

      <h2>17. Contact</h2>
      <p>
        Questions about these Terms? Contact us at{' '}
        <a href="mailto:info@tailwindx.org" className="text-indigo-600 hover:text-indigo-700">info@tailwindx.org</a>.
      </p>
      <p>
        TailwindX, LLC · <a href="https://drscribey.com" className="text-indigo-600 hover:text-indigo-700">drscribey.com</a>
      </p>
    </LegalPage>
  )
}
